<!--
 * COPYRIGHT Transtron Inc 2022
 *
 * 機能名：位置情報
 *
 * 作成者：2021/12/07 ノア）虞
 * 最終修正者：2022/2/12 ノア）黄
 *
 -->
<template>
  <div class="tti-location-dialog">
    <ttiDialog
      :isShow="isShow"
      @handelDisplay="onHandelDisplay"
      :manualKey="manualKey"
      :title="$t('locationInfo.title')"
      :width="24 * 3 + 460 * 2 + 'px'"
    >
      <template slot="body">
        <div class="container">
          <div class="left">
            <el-form :model="searchForm" :rules="searchRules" ref="searchFormRef" label-width="88px">
              <!-- 住所検索 -->
              <el-form-item :label="$t('locationInfo.addressSearch')">
                <div class="radio">
                  <el-radio v-model="searchType" :label="SearchType.ADDRESS">{{ $t('locationInfo.address') }}</el-radio>
                  <el-radio v-model="searchType" :label="SearchType.ZIP_CODE">{{
                    $t('locationInfo.zipcode')
                  }}</el-radio>
                </div>
              </el-form-item>
              <div class="keywordContainer">
                <el-form-item prop="searchKeyword" v-show="searchType === SearchType.ADDRESS">
                  <el-input size="small" v-model.trim="searchForm.searchKeyword"> </el-input>
                  <el-button class="tti-btn-default search-btn tti-btn-002" @click="onSearch">
                    <tti-svg-icon icon-name="su_016"></tti-svg-icon>{{ $t('buttons.search') }}
                  </el-button>
                </el-form-item>
                <el-form-item prop="searchKeyword" v-show="searchType === SearchType.ZIP_CODE">
                  <el-input size="small" v-model.trim="searchForm.searchKeyword"> </el-input>
                  <el-button class="tti-btn-default search-btn tti-btn-002" @click="onSearch">
                    <tti-svg-icon icon-name="su_016"></tti-svg-icon>{{ $t('buttons.search') }}
                  </el-button>
                </el-form-item>
              </div>
              <!-- 住所名称 -->
              <el-form-item :label="$t('locationInfo.addressTitle')">
                <el-table
                  header-row-class-name="tti-master-table-header"
                  :data="addressTableData"
                  :show-header="false"
                  :stripe="true"
                  border
                  height="356px"
                  :empty-text="$t('locationInfo.noData')"
                  highlight-current-row
                  @current-change="handleCurrentChange"
                >
                  <el-table-column prop="addressName"></el-table-column>
                </el-table>
              </el-form-item>
            </el-form>
          </div>
          <div ref="mapContainer" class="right zdc-map"></div>
        </div>
      </template>
    </ttiDialog>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Ref, Prop, Emit } from 'vue-property-decorator'
import { Form } from 'element-ui'

import TtiDialog from '@/components/TtiDialog.vue'
import { dialog } from '@/store/modules'
import { MapService } from '@/commons/services/mapService'
import manualConfig from '@/config/manualConfig'
import { AddressInfo } from '@/models'
import { i18n } from '@/locales'
import { RULES, validateService } from '@/service/validateService'
import config from '@/config/config'

/**
 * 住所検索種類
 *
 * @readonly
 * @constant
 */
enum SearchType {
  ADDRESS = 'address',
  ZIP_CODE = 'zipCode'
}

@Component({
  name: 'location-info',
  components: { TtiDialog }
})
export default class LocationInfo extends Vue {
  /**
   * 検索フォームのref
   */
  @Ref() readonly searchFormRef!: Form

  /**
   * アイコン名
   */
  @Prop({
    default: undefined
  })
  params!: AddressInfo

  @Emit('onGetAdress')
  getAddressEmit(clickedMapAddress: string): string {
    clickedMapAddress = clickedMapAddress.replaceAll('−', '－')
    return clickedMapAddress
  }

  /**
   * 地図サービス
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mapService: any

  /**
   * 住所検索種類
   *
   * @readonly
   * @constant
   */
  SearchType = SearchType

  /**
   * 検索検証ルール
   *
   *  @returns {object} 検索検証ルール
   */
  get searchRules(): Form['rules'] {
    let resultRule: Form['rules'] = {}
    if (this.searchType === SearchType.ADDRESS) {
      resultRule = {
        searchKeyword: [
          // 必須
          {
            required: true,
            message: i18n.t('vaildate.common.required', [i18n.t('locationInfo.address')]).toString(),
            trigger: 'blur'
          },
          // 50文字以内
          {
            validator: validateService.getValidator([
              {
                rule: RULES.MAXLENGTH,
                number: 50,
                message: i18n.t('vaildate.common.maxlength', [i18n.t('locationInfo.address'), 50]).toString()
              }
            ]),
            trigger: 'blur'
          }
        ]
      }
    } else {
      resultRule = {
        searchKeyword: [
          // 必須
          {
            required: true,
            message: i18n.t('vaildate.common.required', [i18n.t('locationInfo.zipcode')]).toString(),
            trigger: 'blur'
          },
          // 7半角数字以内
          {
            validator: validateService.getValidator([
              {
                rule: RULES.MAXLENGTH,
                number: 7,
                message: i18n.t('vaildate.common.maxNumberLength', [i18n.t('locationInfo.zipcode'), 7]).toString()
              }
            ]),
            trigger: 'blur'
          }
        ]
      }
    }

    return resultRule
  }

  /**
   * メッセージ送信サブ画面の表示フラグを取得
   *
   * @returns {boolean} メッセージ送信サブ画面の表示フラグ
   */
  get isShow(): boolean {
    if (dialog.isLocationInfoShow) {
      setTimeout(() => {
        this.mapService = new MapService()

        const mapContainer = this.$refs.mapContainer

        let centerLatLon
        if (!mapContainer) {
          return dialog.isLocationInfoShow
        }

        if (!this.params) {
          centerLatLon = {
            lat: 35.6778614,
            lng: 139.7703167
          }
        }

        if (this.params) {
          if (this.params.addressPosition) {
            centerLatLon = this.params.addressPosition
          } else if (this.params.addressName) {
            // eslint-disable-next-line
            this.mapService.searchAddressInfo(this.params.addressName, false, (data: any) => {
              centerLatLon = data[0]?.point
            })
          } else {
            centerLatLon = {
              lat: 35.6778614,
              lng: 139.7703167
            }
          }
        }

        this.mapService.init(mapContainer as HTMLDivElement, centerLatLon, this.getClickAddress)
      })
    }
    return dialog.isLocationInfoShow
  }

  /**
   * メッセージ送信サブ画面の表示フラグを設定する
   *
   * @param {boolean} value メッセージ送信サブ画面の表示フラグ
   */
  set isShow(value: boolean) {
    dialog.setLocationInfoShow(value)
  }

  /**
   * 住所検索種類
   */
  searchType = SearchType.ADDRESS

  /**
   * 検索情報
   */
  searchForm = {
    searchKeyword: '' // 検索キーワード
  }

  /**
   * 住所名称データ
   */
  addressTableData: AddressInfo[] = []

  selectedAddress: AddressInfo | null = null

  /**
   * 検索
   *
   *  @returns {void}
   */
  onSearch(): void {
    this.searchFormRef.validate((valid: boolean) => {
      if (!valid) return false

      this.doSearch()
    })
  }

  /**
   * 検索
   *
   *  @returns {void}
   */
  doSearch(): void {
    if (this.searchType == SearchType.ADDRESS) {
      if (!this.searchForm.searchKeyword) return
      // eslint-disable-next-line
      this.mapService.searchAddressInfo(this.searchForm.searchKeyword, true, (data: any) => {
        this.addressTableData = []
        if (!data) return
        // eslint-disable-next-line
        this.addressTableData = data.map((item: any) => ({
          addressName: item.text,
          addressPosition: {
            lat: item.point.lat,
            lng: item.point.lon
          }
        }))
      })
    } else if (this.searchType == SearchType.ZIP_CODE) {
      if (!this.searchForm.searchKeyword) return
      // eslint-disable-next-line
      this.mapService.searchZipCodeInfo(this.searchForm.searchKeyword, (data: any) => {
        this.addressTableData = []
        if (!data) return
        // eslint-disable-next-line
        this.addressTableData = data.map((item: any) => ({
          addressName: item.text,
          addressPosition: {
            lat: item.point.lat,
            lng: item.point.lon
          }
        }))
      })
    }
  }

  /**
   * 表示状態変更
   *
   * @param {boolean} value 表示状態
   * @returns {void}
   */
  onHandelDisplay(value: boolean): void {
    dialog.setLocationInfoShow(value)
    this.initForm()
  }

  /**
   * 表示内容を初期化
   *
   * @returns {void}
   */
  initForm(): void {
    this.searchType = SearchType.ADDRESS
    this.searchForm = {
      searchKeyword: ''
    }
    this.addressTableData = []
    this.$nextTick(() => {
      this.searchFormRef.clearValidate()
    })
  }

  /**
   * 位置情報サブ画面のマニュアルキーを取得
   *
   * @returns {string} 位置情報サブ画面のマニュアルキー
   */
  get manualKey(): string {
    return config.menu.managementLocationMaster === this.$route.path
      ? manualConfig.route.locationInfo_mgmt
      : manualConfig.route.locationInfo_sales
  }

  handleCurrentChange(val: AddressInfo): void {
    this.selectedAddress = val

    this.mapService.markLocationPoint(this.selectedAddress)
  }

  // eslint-disable-next-line
  getClickAddress(data: any): void {
    if (!data) return

    const clickedMapAddress = data.text

    this.getAddressEmit(clickedMapAddress)
  }
}
</script>

<style scoped lang="scss">
.container {
  display: flex;
  height: 460px;
  justify-content: space-between;

  .left,
  .right {
    width: 460px;
  }

  .left {
    ::v-deep .el-form-item__label {
      text-align: left;
    }

    ::v-deep .el-radio {
      margin-right: 24px;
    }

    ::v-deep .el-form-item {
      margin-bottom: 0;
    }

    ::v-deep .el-input__inner {
      height: 33px;
      line-height: 33px;
    }

    .keywordContainer {
      ::v-deep .el-form-item__content {
        display: flex;
        align-items: center;
        margin-bottom: 24px;
      }

      .tti-btn-default {
        margin-left: 6px;
      }
    }
  }

  .zdc-map {
    overflow: hidden;
    border: 1px solid $C06;
  }
}

.tti-location-dialog {
  ::v-deep .el-dialog__body {
    padding: 24px;
  }
}
</style>
